/**
 * Returns the correct typography object based on the provided mode.
 * @param {object} config - The configuration object.
 * @param {'preLogin' | 'postLogin'} config.mode - The primary theme mode.
 *
 * @param {object} config.themeOption - The blueprint for the postLogin theme,
 * containing semantic values like `heading`, `textDark`, and `customization` options.
 *
 * @returns A complete MUI typography object.
 */


const getTypography = ({ themeOption, mode }) => {
    // The function uses a switch statement to determine which typography
    // object to return, based on the `mode` received from the theme factory.
    switch (mode) {
        case 'preLogin':
            // If the mode is 'preLogin', we return the simple, static typography object
            // that was originally defined in the pre-login/index.js file.
            return {
                fontFamily: '"Eczar", sans-serif',
                button: {
                    textTransform: 'none',
                    fontWeight: 'medium',
                },
            };

        case 'postLogin':
        default:
            // If the mode is 'postLogin' (or anything else), we return the detailed, dynamic
            // typography object from the original post-login/typography.js file.
            // It uses the `themeOption` blueprint to get its values.
            return {
                fontFamily: themeOption.customization.fontFamily,
                h6: {
                    fontWeight: 500,
                    color: themeOption.heading,
                    fontSize: '0.75rem',
                },
                h5: {
                    fontSize: '0.875rem',
                    color: themeOption.heading,
                    fontWeight: 500,
                },
                h4: {
                    fontSize: '1rem',
                    color: themeOption.heading,
                    fontWeight: 600,
                },
                h3: {
                    fontSize: '1.25rem',
                    color: themeOption.heading,
                    fontWeight: 600,
                },
                h2: {
                    fontSize: '1.5rem',
                    color: themeOption.heading,
                    fontWeight: 700,
                },
                h1: {
                    fontSize: '2.125rem',
                    color: themeOption.heading,
                    fontWeight: 700,
                },
                subtitle1: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: themeOption.textDark,
                },
                subtitle2: {
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    color: themeOption.darkTextSecondary,
                },
                caption: {
                    fontSize: '0.75rem',
                    color: themeOption.darkTextSecondary,
                    fontWeight: 400,
                },
                body1: {
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    lineHeight: '1.334em',
                },
                body2: {
                    letterSpacing: '0em',
                    fontWeight: 400,
                    lineHeight: '1.5em',
                    color: themeOption.darkTextPrimary,
                },
                customInput: {
                    marginTop: 8,
                    marginBottom: 8,
                    '& > label': {
                        top: '23px',
                        left: 0,
                        color: themeOption.colors.grey500,
                        '&[data-shrink="false"]': {
                            top: '5px',
                        },
                    },
                    '& > div > input': {
                        padding: '30.5px 14px 11.5px !important',
                    },
                    '& legend': {
                        display: 'none',
                    },
                    '& fieldset': {
                        top: 0,
                    },
                },
                mainContent: {
                    backgroundColor: themeOption.background,
                    width: '100%',
                    minHeight: 'calc(100vh - 88px)',
                    flexGrow: 1,
                    padding: '20px',
                    marginTop: '88px',
                    marginRight: '20px',
                    borderRadius: `${themeOption.customization.borderRadius}px`,
                },
                menuCaption: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: themeOption.heading,
                    padding: '6px',
                    textTransform: 'capitalize',
                    marginTop: '10px',
                },
                subMenuCaption: {
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    color: themeOption.darkTextSecondary,
                    textTransform: 'capitalize',
                },
                commonAvatar: {
                    cursor: 'pointer',
                    borderRadius: '8px',
                },
                smallAvatar: {
                    width: '22px',
                    height: '22px',
                    fontSize: '1rem',
                },
                mediumAvatar: {
                    width: '34px',
                    height: '34px',
                    fontSize: '1.2rem',
                },
                largeAvatar: {
                    width: '44px',
                    height: '44px',
                    fontSize: '1.5rem',
                },
            };
    }
};

export default getTypography;